import {
	ProjectFilterValues,
	ProjectsResponse,
	PropositionProductType,
} from "@dune/types";
import { UnderlineTabs } from "@dune/ui";
import { projectFilter } from "@dune/vars";
import { useMemo } from "react";
import { useProductTypeTabs } from "../../../component/common/useProductTypeTabs";

interface ProjectTabsProps {
	activeFilter: ProjectFilterValues;
	onFilterChange: (filter: ProjectFilterValues) => void;
	projectsData?: ProjectsResponse;
	isLoading: boolean;
}

const mapProductTypeToProjectFilter = (
	productType: string | undefined,
): ProjectFilterValues => {
	if (!productType) return projectFilter.TOUS;
	switch (productType) {
		case PropositionProductType.DO:
			return projectFilter.DO;
		case PropositionProductType.TRC:
			return projectFilter.TRC;
		case PropositionProductType.MRT:
			return projectFilter.MRT;
		case PropositionProductType.GFA:
			return projectFilter.GFA;
		default:
			return projectFilter.TOUS;
	}
};

export const ProjectTabs: React.FC<ProjectTabsProps> = ({
	activeFilter,
	onFilterChange,
	projectsData,
	isLoading,
}) => {
	const productTypeCounts = useMemo(() => {
		if (isLoading || !projectsData) {
			return {
				all: 0,
				[PropositionProductType.DO]: 0,
				[PropositionProductType.TRC]: 0,
				[PropositionProductType.MRT]: 0,
				[PropositionProductType.GFA]: 0,
			};
		}

		return {
			all: projectsData.totalCounts.tous || 0,
			[PropositionProductType.DO]: projectsData.totalCounts.do || 0,
			[PropositionProductType.TRC]: projectsData.totalCounts.trc || 0,
			[PropositionProductType.MRT]: projectsData.totalCounts.mrt || 0,
			[PropositionProductType.GFA]: projectsData.totalCounts.gfa || 0,
		};
	}, [projectsData, isLoading]);

	const { productTypeTabs, shouldShowProductTypeTabs } =
		useProductTypeTabs(productTypeCounts);

	const tabs = useMemo(() => {
		return productTypeTabs.map((tab) => ({
			value: mapProductTypeToProjectFilter(tab.value),
			label: tab.label,
			count: tab.count,
		}));
	}, [productTypeTabs]);

	if (!shouldShowProductTypeTabs) {
		return null;
	}

	return (
		<div className="grid justify-start">
			<UnderlineTabs
				tabs={tabs}
				value={activeFilter}
				onValueChange={(value) => onFilterChange(value as ProjectFilterValues)}
				className="w-full"
			/>
		</div>
	);
};
